import { VIEWS, DIALOGS } from './chat-screen-state';

const PUBLIC_GROUP_SEARCH_LIMIT = 50;

function memberSelfLabel() {
  return window.i18n.t('chat.manage_members.you');
}

// Current user first, then other admins, then everyone else. Stable within each group
// so a name search does not reshuffle people who share a rank.
function sortGroupMembers(members, currentUserId) {
  const rank = (member) => {
    if (member.user_id === currentUserId) return 0;
    if (member.role === 'admin') return 1;
    return 2;
  };
  return [...members].sort((a, b) => rank(a) - rank(b));
}

export function groupActions(currentUserId) {
  return {
    completedUserSearchQuery: null,
    userSearchRequestId: 0,
    publicGroupSearchRequestId: 0,

    hasNoMatchingContacts() {
      return this.userSearchQuery.length >= 2
        && this.completedUserSearchQuery === this.userSearchQuery
        && !this.isSearchingUsers
        && this.userSearchResults.length === 0;
    },
    // ─────────────────────────────────────────
    // New Message / Group Creation
    // ─────────────────────────────────────────
    openNewMessageDialog() {
      this.view = VIEWS.NEW_MESSAGE;
      this.newGroupName = '';
      this.newGroupIsPublic = false;
      this.newGroupDescription = '';
      this.newGroupJoinPolicy = 'open';
      this.selectedUsers = [];
      this.userSearchQuery = '';
      this.userSearchResults = [];
      this.completedUserSearchQuery = null;
      this.duplicateGroup = null;
      this.duplicateGroupName = null;
      this.duplicateMembersRequestId += 1;
      this.error = null;
      this.loadSuggestedUsers();
    },

    openNewGroupDialog() {
      this.view = VIEWS.NEW_GROUP;
      this.newGroupName = '';
      this.newGroupIsPublic = false;
      this.newGroupDescription = '';
      this.newGroupJoinPolicy = 'open';
      this.selectedUsers = [];
      this.userSearchQuery = '';
      this.userSearchResults = [];
      this.duplicateGroup = null;
      this.duplicateGroupName = null;
      this.duplicateMembersRequestId += 1;
      this.error = null;
      this.loadSuggestedUsers();
    },

    closeNewGroupDialog() {
      this.view = VIEWS.LIST;
      this.conversationFilter = 'groups';
    },

    closeNewMessageDialog() {
      this.view = VIEWS.LIST;
    },

    async loadSuggestedUsers() {
      this.isLoadingSuggested = true;
      try {
        const response = await this.$api({
          url: '/chat/users',
          method: 'GET',
          query: { per_page: 10 }
        });
        this.suggestedUsers = (response.data.data || []).map((u) => ({
          id: u.id,
          name: u.attributes.name,
          chat_status: u.attributes.chat_status,
          profile_image_url: u.links?.profile_image_thumbnail
        }));
      } catch (err) {
        console.error('Failed to load suggested users:', err);
        this.suggestedUsers = [];
      } finally {
        this.isLoadingSuggested = false;
      }
    },

    async searchUsers() {
      const requestId = ++this.userSearchRequestId;
      const query = this.userSearchQuery;
      this.completedUserSearchQuery = null;
      // When query is cleared, show suggested users
      if (query.length < 2) {
        this.userSearchResults = [];
        this.isSearchingUsers = false;
        return;
      }

      this.isSearchingUsers = true;
      try {
        const response = await this.$api({
          url: '/chat/users',
          method: 'GET',
          query: { name: query, per_page: 10 }
        });
        // A slower response must not replace results for a newer search.
        if (requestId !== this.userSearchRequestId || query !== this.userSearchQuery) return;
        // Parse JSON API format
        const users = (response.data.data || []).map((u) => ({
          id: u.id,
          name: u.attributes.name,
          chat_status: u.attributes.chat_status,
          profile_image_url: u.links?.profile_image_thumbnail
        }));

        // Keep people who are already in the group in the results, marked so the row can
        // explain they cannot be added again. New chats have no existing members to mark.
        const conv = this.addMembersDialogOpen && this.activeConversationId ? this.getConversation(this.activeConversationId) : null;
        const existingMemberIds = conv ? (conv.members || []).map((m) => m.user_id) : [];
        const selectedIds = this.selectedUsers.map((u) => u.id);

        this.userSearchResults = users
          .filter((u) => u.id !== currentUserId && !selectedIds.includes(u.id))
          .map((u) => ({
            ...u,
            already_member: existingMemberIds.includes(u.id)
          }));
        this.completedUserSearchQuery = query;
      } catch (err) {
        console.error('Failed to search users:', err);
        if (requestId === this.userSearchRequestId) this.userSearchResults = [];
      } finally {
        if (requestId === this.userSearchRequestId) this.isSearchingUsers = false;
      }
    },

    toggleUserSelection(user) {
      if (this.isUserSelected(user.id)) {
        this.selectedUsers = this.selectedUsers.filter((u) => u.id !== user.id);
      } else {
        this.userSearchQuery = '';
        this.userSearchResults = [];
        this.selectedUsers.push(user);
      }
      this.findDuplicateGroup();
    },

    isUserSelected(userId) {
      return this.selectedUsers.some((u) => u.id === userId);
    },

    removeSelectedUser(userId) {
      this.selectedUsers = this.selectedUsers.filter((u) => u.id !== userId);
      this.findDuplicateGroup();
    },

    selectUser(user) {
      if (!user || user.already_member) return;
      if (!this.selectedUsers.some((u) => u.id === user.id)) {
        this.selectedUsers.push(user);
      }
      this.userSearchQuery = '';
      this.userSearchResults = [];
      this.findDuplicateGroup();
    },

    async findDuplicateGroupName() {
      const name = this.newGroupName.trim();
      if (!name || !this.newGroupIsPublic) {
        this.duplicateGroupName = null;
        return;
      }

      try {
        const response = await this.$api({
          url: '/chat/conversations/check_name',
          method: 'GET',
          query: { name }
        });
        const { taken, suggestion } = response.data;
        this.duplicateGroupName = taken ? suggestion : null;
      } catch (err) {
        this.duplicateGroupName = null;
      }
    },

    async findDuplicateGroup() {
      if (!this.shouldCheckDuplicateMembers()) {
        this.duplicateGroup = null;
        return;
      }

      const participantIds = this.selectedUsers.map((u) => u.id).join(',');
      const requestId = ++this.duplicateMembersRequestId;

      try {
        const response = await this.$api({
          url: '/chat/conversations/check_members',
          method: 'GET',
          query: { participant_ids: participantIds }
        });
        if (requestId !== this.duplicateMembersRequestId) return;
        this.duplicateGroup = response.data.conversation || null;
      } catch {
        if (requestId !== this.duplicateMembersRequestId) return;
        this.duplicateGroup = null;
      }
    },

    shouldCheckDuplicateMembers() {
      if (this.newGroupIsPublic) return false;
      if (this.selectedUsers.length < 1) return false;
      // New Message treats a single recipient as a DM, not a group
      if (this.view === VIEWS.NEW_MESSAGE && this.selectedUsers.length < 2) return false;
      return true;
    },

    async openExistingDuplicateGroup() {
      const conv = this.duplicateGroup;
      if (!conv) return;

      if (this.view === VIEWS.NEW_GROUP) {
        this.closeNewGroupDialog();
      } else {
        this.closeNewMessageDialog();
      }

      if (!this.getConversation(conv.id)) {
        if (conv.is_hidden) {
          this.hiddenConversations = [conv, ...this.hiddenConversations];
        } else {
          this.conversations = [conv, ...this.conversations];
        }
      }

      this.openConversation(conv.id, { source: 'new group' });
    },

    isDM() {
      return this.selectedUsers.length === 1;
    },

    canCreate() {
      if (this.isCreatingGroup) return false;
      if (this.selectedUsers.length === 0) return false;
      // DM: always valid with 1 user
      if (this.selectedUsers.length === 1) return true;
      // Group: requires name
      return this.newGroupName.trim().length > 0;
    },

    async startChat() {
      if (!this.canCreate()) return;
      if (this.isDM()) {
        this.closeNewMessageDialog();
        await this.openConversationWithUser(this.selectedUsers[0].id, { source: 'new dm' });
        return;
      }

      this.isCreatingGroup = true;
      try {
        await this.findDuplicateGroup();
        if (this.duplicateGroup) return;

        const response = await this.$api({
          url: '/chat/conversations',
          method: 'POST',
          data: {
            conversation_type: 'group',
            name: this.newGroupName.trim(),
            participant_ids: this.selectedUsers.map((u) => u.id),
            is_public: this.newGroupIsPublic,
            description: this.newGroupIsPublic ? this.newGroupDescription.trim() : null,
            join_policy: this.newGroupJoinPolicy
          }
        });

        this.trackChat('group', 'create');
        const conversation = response.data.conversation;
        if (!this.conversations.some((c) => c.id === conversation.id)) {
          this.conversations.unshift(conversation);
        }
        this.closeNewMessageDialog();
        this.openConversation(conversation.id, { source: 'new group' });
      } catch (resp) {
        console.error('Failed to create group:', resp);
        try {
          const body = await resp.json();
          this.error = body.errors?.[0] || 'Failed to create group';
        } catch {
          this.error = 'Failed to create group';
        }
      } finally {
        this.isCreatingGroup = false;
      }
    },

    // Legacy aliases for backward compatibility with group management UI
    canCreateGroup() {
      return this.canCreate();
    },

    async createGroup() {
      return this.startChat();
    },

    canCreateNewGroup() {
      if (this.isCreatingGroup) return false;
      if (this.newGroupName.trim().length === 0) return false;
      // Private groups require at least one other participant (matches server validation)
      if (!this.newGroupIsPublic && this.selectedUsers.length === 0) return false;
      return true;
    },

    async createNewGroup() {
      if (!this.canCreateNewGroup()) return;

      this.isCreatingGroup = true;
      try {
        await this.findDuplicateGroup();
        if (this.duplicateGroup) return;

        const response = await this.$api({
          url: '/chat/conversations',
          method: 'POST',
          data: {
            conversation_type: 'group',
            name: this.newGroupName.trim(),
            participant_ids: this.selectedUsers.map((u) => u.id),
            is_public: this.newGroupIsPublic,
            description: this.newGroupIsPublic ? this.newGroupDescription.trim() : null,
            join_policy: this.newGroupJoinPolicy
          }
        });

        this.trackChat('group', 'create');
        const conversation = response.data.conversation;
        if (!this.conversations.some((c) => c.id === conversation.id)) {
          this.conversations.unshift(conversation);
        }
        this.closeNewGroupDialog();
        this.openConversation(conversation.id, { source: 'new group' });
      } catch (resp) {
        console.error('Failed to create group:', resp);
        try {
          const body = await resp.json();
          this.error = body.errors?.[0] || 'Failed to create group';
        } catch {
          this.error = 'Failed to create group';
        }
      } finally {
        this.isCreatingGroup = false;
      }
    },

    // ─────────────────────────────────────────
    // Public Groups Browse
    // ─────────────────────────────────────────
    myGroups() {
      return this.conversations.filter(
        (c) => c.conversation_type === 'group' && c.current_user_status === 'active'
      );
    },

    visiblePublicGroups() {
      return this.publicGroupSearchQuery.trim() ? this.publicGroupSearchResults : this.publicGroups;
    },

    async searchPublicGroups() {
      const requestId = ++this.publicGroupSearchRequestId;
      const query = this.publicGroupSearchQuery.trim();
      this.publicGroupSearchResults = [];
      if (!query) {
        this.isSearchingPublicGroups = false;
        return;
      }

      this.isSearchingPublicGroups = true;
      try {
        const response = await this.$api({
          url: '/chat/search',
          method: 'GET',
          query: { scope: 'public_groups', query, limit: PUBLIC_GROUP_SEARCH_LIMIT }
        });
        // A slower response must not replace results for a newer search.
        if (requestId !== this.publicGroupSearchRequestId) return;
        this.publicGroupSearchResults = response.data.groups || [];
      } catch (err) {
        console.error('Failed to search public groups:', err);
      } finally {
        if (requestId === this.publicGroupSearchRequestId) this.isSearchingPublicGroups = false;
      }
    },

    removeFromPublicGroups(conversationId) {
      this.publicGroups = this.publicGroups.filter((g) => g.id !== conversationId);
      this.publicGroupSearchResults = this.publicGroupSearchResults.filter((g) => g.id !== conversationId);
    },

    async loadPublicGroups() {
      this.publicGroupsShowAll = false;
      await this.syncPublicGroupsList({ showLoading: true });
    },

    async syncPublicGroupsList({ showLoading = false } = {}) {
      if (showLoading) this.isLoadingPublicGroups = true;
      try {
        const response = await this.$api({
          url: '/chat/conversations',
          method: 'GET',
          query: { scope: 'public_groups' }
        });
        const myGroupIds = new Set(this.myGroups().map((g) => g.id));
        this.publicGroups = (response.data.conversations || []).filter(
          (g) => !myGroupIds.has(g.id) && g.current_user_status !== 'active' && g.current_user_status !== 'removed'
        );
      } catch (err) {
        console.error('Failed to load public groups:', err);
        this.publicGroups = [];
      } finally {
        if (showLoading) this.isLoadingPublicGroups = false;
      }
    },

    // Approval, bulk-add, or join from another client — sync My Groups and Find Groups.
    async handleMemberAdded(conversationId) {
      const conv = this.conversations.find((c) => c.id === conversationId);
      if (conv) {
        conv.is_removed = false;
        conv.current_user_status = 'active';
      }

      this.removeFromPublicGroups(conversationId);
      if (this.pendingGroupIds.has(conversationId)) {
        const next = new Set(this.pendingGroupIds);
        next.delete(conversationId);
        this.pendingGroupIds = next;
      }

      await this.reloadConversation(conversationId);

      if (this.conversationFilter === 'groups' || this.publicGroups.length > 0) {
        await this.syncPublicGroupsList();
      }
    },

    openPublicGroupsView() {
      this.trackChat('public groups', 'open');
      this.view = VIEWS.PUBLIC_GROUPS;
      this.publicGroupSearchQuery = '';
      this.requestingGroupId = null;
      this.requestNote = '';
      this.requestSubmitted = false;
      this.loadPublicGroups();
    },

    closePublicGroupsView() {
      this.view = VIEWS.LIST;
      this.conversationFilter = 'groups';
    },

    openRequestToJoin(groupId) {
      this.requestingGroupId = groupId;
      this.requestNote = '';
      this.requestSubmitted = false;
    },

    cancelRequest() {
      this.requestingGroupId = null;
      this.requestNote = '';
    },

    async sendJoinRequest(groupId = this.requestingGroupId) {
      if (!groupId || this.isSendingRequest) return;
      this.isSendingRequest = true;
      try {
        await this.$api({
          url: `/chat/conversations/${groupId}/request_to_join`,
          method: 'POST',
          data: { note: this.requestNote.trim() }
        });
        this.trackChat('group', 'request to join');
        this.pendingGroupIds = new Set([...this.pendingGroupIds, groupId]);
        this.requestSubmitted = true;
        this.requestingGroupId = null;
        setTimeout(() => { this.requestSubmitted = false; }, 4000);
      } catch (err) {
        console.error('Failed to send join request:', err);
      } finally {
        this.isSendingRequest = false;
      }
    },

    groupJoinLabel(group) {
      if (this.pendingGroupIds.has(group.id) || group.current_user_status === 'pending') return 'pending';
      if (group.current_user_status === 'rejected') return 'rejected';
      if (group.current_user_status === 'active') return 'member';
      if (group.join_policy === 'approval') return 'request';
      return 'join';
    },

    requestNoteWordCount() {
      return this.requestNote.trim() === '' ? 0 : this.requestNote.trim().split(/\s+/).length;
    },

    // ─────────────────────────────────────────
    // Group Management
    // ─────────────────────────────────────────
    toggleGroupInfo() {
      this.dialog = this.dialog === DIALOGS.GROUP_INFO ? null : DIALOGS.GROUP_INFO;
    },

    toggleGroupActionsMenu() {
      this.dialog = this.dialog === DIALOGS.GROUP_ACTIONS ? null : DIALOGS.GROUP_ACTIONS;
    },

    closeGroupActionsMenu() {
      if (this.dialog === DIALOGS.GROUP_ACTIONS) this.dialog = null;
    },

    toggleGroupMembersPreview() {
      this.dialog = this.dialog === DIALOGS.GROUP_MEMBERS_PREVIEW ? null : DIALOGS.GROUP_MEMBERS_PREVIEW;
    },

    closeGroupMembersPreview() {
      if (this.dialog === DIALOGS.GROUP_MEMBERS_PREVIEW) this.dialog = null;
    },

    groupMembersPreviewList() {
      const conv = this.getConversation(this.activeConversationId);
      return sortGroupMembers(conv?.members || [], this.currentUserId).slice(0, 8);
    },

    memberDisplayName(member) {
      if (!member) return '';
      if (member.user_id === this.currentUserId) return memberSelfLabel();
      return member.name || '';
    },

    groupMemberCount() {
      const conv = this.getConversation(this.activeConversationId);
      return (conv?.members || []).length;
    },

    groupOtherMemberCount() {
      return Math.max(0, this.groupMemberCount() - 1);
    },

    openManageMembers() {
      this.dialog = null;
      this.manageMembersSearchQuery = '';
      this.selectedMember = null;
      this.removeMemberConfirm = false;
      this.view = VIEWS.MANAGE_MEMBERS;
    },

    closeManageMembers() {
      this.manageMembersSearchQuery = '';
      this.selectedMember = null;
      this.removeMemberConfirm = false;
      this.view = VIEWS.CONVERSATION;
    },

    openMemberActions(member) {
      this.selectedMember = member;
      this.removeMemberConfirm = false;
    },

    closeMemberActions() {
      this.selectedMember = null;
      this.removeMemberConfirm = false;
    },

    openRemoveMemberConfirm() {
      this.removeMemberConfirm = true;
    },

    async confirmRemoveMember() {
      if (!this.selectedMember) return;
      const membershipId = this.selectedMember.id;
      this.closeMemberActions();
      await this.removeMember(membershipId);
    },

    async makeGroupAdmin(member) {
      if (!member) return;
      try {
        await this.$api({
          url: `/chat/conversations/${this.activeConversationId}/memberships/${member.id}`,
          method: 'PATCH',
          data: { role: 'admin' }
        });
        this.closeMemberActions();
        await this.reloadConversation(this.activeConversationId);
      } catch (err) {
        console.error('Failed to make admin:', err);
        this.error = 'Failed to make group admin';
      }
    },

    async messageMember(member) {
      if (!member) return;
      this.closeMemberActions();
      this.closeManageMembers();
      await this.openConversationWithUser(member.user_id, { source: 'member' });
    },

    filteredGroupMembers() {
      const conv = this.getConversation(this.activeConversationId);
      const members = conv?.members || [];
      const q = (this.manageMembersSearchQuery || '').toLowerCase().trim();
      const selfLabel = memberSelfLabel().toLowerCase();
      const visible = !q
        ? members
        : members.filter((m) => {
          if (m.name?.toLowerCase().includes(q)) return true;
          return m.user_id === this.currentUserId && selfLabel.includes(q);
        });
      return sortGroupMembers(visible, this.currentUserId);
    },

    deactivateGroup() {
      if (!this.isGroupAdmin()) return;
      this.dialog = null;
      this.deactivateGroupConfirm = true;
    },

    cancelDeactivateGroup() {
      this.deactivateGroupConfirm = false;
    },

    async confirmDeactivateGroup() {
      if (!this.isGroupAdmin()) return;
      const conversationId = this.activeConversationId;
      this.deactivateGroupConfirm = false;
      try {
        const response = await this.$api({
          url: `/chat/conversations/${conversationId}/deactivate`,
          method: 'POST'
        });
        // Update the conversation in-place so everyone sees the deactivated state
        const updated = response.data.conversation;
        const index = this.conversations.findIndex((c) => c.id === conversationId);
        if (index !== -1) {
          this.conversations[index] = { ...this.conversations[index], ...updated };
        }
      } catch (err) {
        console.error('Failed to deactivate group:', err);
        this.error = 'Failed to deactivate group';
      }
    },

    reactivateGroup() {
      if (!this.isGroupAdmin()) return;
      this.dialog = null;
      this.reactivateGroupConfirm = true;
    },

    cancelReactivateGroup() {
      this.reactivateGroupConfirm = false;
    },

    async confirmReactivateGroup() {
      if (!this.isGroupAdmin()) return;
      const conversationId = this.activeConversationId;
      this.reactivateGroupConfirm = false;
      try {
        const response = await this.$api({
          url: `/chat/conversations/${conversationId}/reactivate`,
          method: 'POST'
        });
        const updated = response.data.conversation;
        const index = this.conversations.findIndex((c) => c.id === conversationId);
        if (index !== -1) {
          this.conversations[index] = { ...this.conversations[index], ...updated };
        }
      } catch (err) {
        console.error('Failed to reactivate group:', err);
        this.error = 'Failed to reactivate group';
      }
    },

    openAddMembersDialog() {
      this.addMembersDialogOpen = true;
      this.addMembersError = null;
      this.userSearchQuery = '';
      this.userSearchResults = [];
      this.selectedUsers = [];
    },

    closeAddMembersDialog() {
      this.addMembersDialogOpen = false;
      this.addMembersError = null;
      this.userSearchQuery = '';
      this.userSearchResults = [];
      this.selectedUsers = [];
    },

    async confirmAddMembers() {
      if (this.selectedUsers.length === 0) return;
      this.isAddingMembers = true;
      this.addMembersError = null;
      try {
        const response = await this.$api({
          url: `/chat/conversations/${this.activeConversationId}/memberships/bulk_add`,
          method: 'POST',
          data: { user_ids: this.selectedUsers.map((u) => u.id) }
        });
        const { errors } = response.data;
        await this.reloadConversation(this.activeConversationId);
        if (errors && errors.length > 0) {
          this.addMembersError = errors.map((e) => e.error).join(', ');
        } else {
          this.closeAddMembersDialog();
        }
      } catch (err) {
        console.error('Failed to add members:', err);
        this.addMembersError = 'Failed to add members';
      } finally {
        this.isAddingMembers = false;
      }
    },

    async removeMember(membershipId) {
      try {
        await this.$api({
          url: `/chat/conversations/${this.activeConversationId}/memberships/${membershipId}`,
          method: 'DELETE'
        });
        await this.reloadConversation(this.activeConversationId);
      } catch (err) {
        console.error('Failed to remove member:', err);
        this.error = err.response?.data?.error || 'Failed to remove member';
      }
    },

    async leaveGroup() {
      const conversationId = this.activeConversationId;
      try {
        await this.$api({
          url: `/chat/conversations/${conversationId}/leave`,
          method: 'POST'
        });
        this.trackChat('group', 'leave');
        this.closeConversation();
        this.conversations = this.conversations.filter((c) => c.id !== conversationId);

        // Notify other components (e.g., search results) that user left
        this.$dispatch('chat:left', { conversationId });
      } catch (err) {
        console.error('Failed to leave group:', err);
        this.error = err.response?.data?.error || 'Failed to leave group';
      }
    },

    async reloadConversation(conversationId) {
      try {
        const response = await this.$api({
          url: `/chat/conversations/${conversationId}`,
          method: 'GET'
        });
        this.upsertConversation(response.data.conversation);
      } catch (err) {
        console.error('Failed to reload conversation:', err);
      }
    },

    // Keep a directly fetched thread available even when it is outside the
    // currently loaded inbox / hidden / blocked list window.
    upsertConversation(updated) {
      const conversationId = updated.id;
      const replaceIn = (list) => list.map((c) => (c.id === conversationId ? { ...c, ...updated } : c));
      const removeFrom = (list) => list.filter((c) => c.id !== conversationId);

      if (updated.blocked_by_me) {
        this.conversations = removeFrom(this.conversations);
        this.hiddenConversations = removeFrom(this.hiddenConversations);
        this.blockedConversations = this.blockedConversations.some((c) => c.id === conversationId)
          ? replaceIn(this.blockedConversations)
          : [updated, ...this.blockedConversations];
        return;
      }

      this.blockedConversations = removeFrom(this.blockedConversations);

      const hiddenIndex = this.hiddenConversations.findIndex((c) => c.id === conversationId);
      if (updated.is_hidden || hiddenIndex !== -1) {
        this.conversations = removeFrom(this.conversations);
        this.hiddenConversations = hiddenIndex !== -1
          ? replaceIn(this.hiddenConversations)
          : [updated, ...this.hiddenConversations];
        return;
      }

      const index = this.conversations.findIndex((c) => c.id === conversationId);
      if (index !== -1) {
        this.conversations = replaceIn(this.conversations);
      } else {
        this.conversations = [updated, ...this.conversations];
      }
    },

    // Defaults to the active conversation; pass a conversation to test a
    // specific one (e.g. a list row).
    isGroupConversation(conversation = this.getConversation(this.activeConversationId)) {
      return conversation?.conversation_type === 'group';
    },

    isGroupAdmin() {
      const conv = this.getConversation(this.activeConversationId);
      return conv?.is_admin === true;
    },

    isGroupCreator() {
      const conv = this.getConversation(this.activeConversationId);
      return conv?.created_by_id === this.currentUserId;
    },

    isRemovedFromGroup() {
      const conv = this.getConversation(this.activeConversationId);
      return conv?.is_removed === true;
    },

    isGroupDeactivated() {
      const conv = this.getConversation(this.activeConversationId);
      return conv?.is_deactivated === true;
    },

    async demoteGroupAdmin(member) {
      if (!member) return;
      try {
        await this.$api({
          url: `/chat/conversations/${this.activeConversationId}/memberships/${member.id}`,
          method: 'PATCH',
          data: { role: 'member' }
        });
        this.closeMemberActions();
        await this.reloadConversation(this.activeConversationId);
      } catch (err) {
        console.error('Failed to remove admin:', err);
        this.error = 'Failed to remove admin role';
      }
    }
  };
}
